import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { map, shareReplay, take, filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { RoleEnum } from '../../models';

@Injectable()
export class RoleService {
  private roleCache: Observable<{ role: RoleEnum }>;

  constructor(private authService: AuthService) {
    this.buildCache();
  }

  getCurrentUserRole(): Observable<{ role: RoleEnum }> {
    return this.roleCache;
  }

  mapRole(role: string): RoleEnum {
    return (role.split(':')[1] as RoleEnum) || null;
  }

  getAuditIdFromUserRole(): Observable<{ audit_id: string }> {
    return from(this.authService.getUserAsync()).pipe(
      map((user) => ({
        audit_id: user?.role.split(':')[2] || null,
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  isAuditorZone(): Promise<boolean> {
    return firstValueFrom(this.getCurrentUserRole()
      .pipe(map((currentUser) => currentUser.role === RoleEnum.Zone)));
  }

  isAdmin(): Observable<boolean> {
    return this.getCurrentUserRole()
      .pipe(
        take(1),
        map((currentUser) => currentUser.role === RoleEnum.Admin)
      );
  }

  isAuditor(): Observable<boolean> {
    return this.getCurrentUserRole()
      .pipe(
        take(1),
        map((currentUser) => currentUser.role === RoleEnum.Auditor)
      );
  }

  isCollaborator(): Observable<boolean> {
    return this.getCurrentUserRole()
      .pipe(
        take(1),
        map((currentUser) => currentUser.role === RoleEnum.Collaborator)
      );
  }

  isStakeholderType(): Observable<boolean> {
    return this.getCurrentUserRole()
      .pipe(
        take(1),
        map(({ role }) => [
          RoleEnum.Stakeholder,
          RoleEnum.It,
          RoleEnum.User,
          RoleEnum.Reviewer
        ].includes(role))
      );
  }

  private buildCache(): void {
    this.roleCache = this.authService.getUser().pipe(
      filter((user) => Boolean(user)),
      map((user) => ({
        role: user.role ? this.mapRole(user.role) : undefined,
      })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
